import React, { useEffect, useState } from "react";
import apiClient from "../api/api";
import "../styles/EmployeeAttendance.css";

const STANDARD_HOURS_PER_DAY = 8.5;

const StatisticsPage = () => {
  const [statistics, setStatistics] = useState({
    today: 0,
    week: 0,
    month: 0,
    overtime: 0,
    total: 0,
    expectedToday: STANDARD_HOURS_PER_DAY,
    expectedWeek: 0,
    expectedMonth: 0,
  });

  const fetchAttendanceData = async () => {
    try {
      const response = await apiClient.get("/attendance");
      return response.data;
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      return [];
    }
  };

  const fetchPublicHolidays = async () => {
    try {
      const response = await apiClient.get("/holidays");
      return response.data.map(holiday => holiday.date);
    } catch (error) {
      console.error("Error fetching holidays data:", error);
      return [];
    }
  };

  const normalizeEntry = (entry) => ({
    date: entry.date,
    checkIn: entry.clock_in,
    checkOut: entry.clock_out,
    breaks: entry.break_start && entry.break_end
      ? [{ breakStart: entry.break_start, breakEnd: entry.break_end }]
      : [],
  });

  const formatHours = (decimalHours) => {
    // Prevent negative values
    const safeHours = Math.max(0, decimalHours);
    const hours = Math.floor(safeHours);
    const minutes = Math.round((safeHours - hours) * 60);
    return `${hours}h ${minutes}m`;
  };

  const getWorkedHours = (entries) =>
    entries.reduce((sum, entry) => {
      // Check for missing clock times
      if (!entry.checkIn || !entry.checkOut) return sum;

      // Check for sentinel clock-out dates (auto clocked out records)
      const isSentinelClockOut = entry.checkOut === '2000-01-01T00:00:00.000Z' ||
                                entry.checkOut?.startsWith('2000-01-01');
      if (isSentinelClockOut) return sum; // Exclude auto clock-out records

      const checkIn = new Date(entry.checkIn);
      const checkOut = new Date(entry.checkOut);
      if (isNaN(checkIn) || isNaN(checkOut)) return sum;

      let duration = checkOut - checkIn;

      if (Array.isArray(entry.breaks)) {
        const totalBreakMs = entry.breaks.reduce((bSum, br) => {
          if (!br.breakStart || !br.breakEnd) return bSum;
          const bStart = new Date(br.breakStart);
          const bEnd = new Date(br.breakEnd);
          return bSum + (bEnd - bStart);
        }, 0);
        duration -= totalBreakMs;
      }

      // Only add positive durations (extra safety net)
      const hoursDuration = duration / (1000 * 60 * 60);
      return hoursDuration > 0 ? sum + hoursDuration : sum;
    }, 0);

  useEffect(() => {
    const fetchData = async () => {
      const rawData = await fetchAttendanceData();
      const publicHolidayList = await fetchPublicHolidays();


      const normalizedData = rawData.map(normalizeEntry);

      const now = new Date();
      const todayStr = now.toISOString().split("T")[0];

      // Today
      const todayEntries = normalizedData.filter(e => e.date === todayStr);
      const hoursToday = getWorkedHours(todayEntries);

      // Week
      const weekStart = new Date(now);
      weekStart.setDate(weekStart.getDate() - (weekStart.getDay() === 0 ? 6 : weekStart.getDay() - 1));
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 4);

      const weekEntries = normalizedData.filter(e => {
        const d = new Date(e.date);
        return d >= weekStart && d <= weekEnd;
      });

      const weekHolidays = publicHolidayList.filter(dateStr => {
        const d = new Date(dateStr);
        return d >= weekStart && d <= weekEnd;
      });

      const expectedWeekHours = STANDARD_HOURS_PER_DAY * (5 - weekHolidays.length);
      const hoursWeek = getWorkedHours(weekEntries);

      // DEBUG: Log weekly calculations
      console.log(`📊 EMPLOYEE DASHBOARD - This Week: ${hoursWeek.toFixed(2)}h / ${expectedWeekHours.toFixed(2)}h expected`);
      console.log("  Week entries:", weekEntries.map(e => ({
        date: e.date,
        checkIn: e.checkIn,
        checkOut: e.checkOut,
        duration: e.checkIn && e.checkOut ? (new Date(e.checkOut) - new Date(e.checkIn)) / (1000 * 60 * 60) : 'no data'
      })));

      // Month
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const monthEntries = normalizedData.filter(e => {
        const d = new Date(e.date);
        return d.getMonth() === currentMonth && d.getFullYear() === currentYear;
      });

      const workingDaysInMonth = [];
      const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
      for (let i = 1; i <= daysInMonth; i++) {
        const day = new Date(currentYear, currentMonth, i);
        const isWeekend = day.getDay() === 0 || day.getDay() === 6;
        const isHoliday = publicHolidayList.includes(day.toISOString().split("T")[0]);
        if (!isWeekend && !isHoliday) {
          workingDaysInMonth.push(day);
        }
      }

      const expectedMonthHours = STANDARD_HOURS_PER_DAY * workingDaysInMonth.length;
      const hoursMonth = getWorkedHours(monthEntries);

      // Overtime
      const overtimeEntries = monthEntries.filter(e => {
        const d = new Date(e.date);
        const isWeekend = d.getDay() === 0 || d.getDay() === 6;
        const isHoliday = publicHolidayList.includes(e.date);
        return isWeekend || isHoliday;
      });

      const overtime = getWorkedHours(overtimeEntries);
      const total = hoursMonth + overtime;

      setStatistics({
        today: hoursToday,
        week: hoursWeek,
        month: hoursMonth,
        overtime,
        total,
        expectedToday: STANDARD_HOURS_PER_DAY,
        expectedWeek: expectedWeekHours,
        expectedMonth: expectedMonthHours,
      });
    };

    fetchData();
  }, []);

  const StatCard = ({ label, value, colorClass, progress }) => {
    // Cap progress at 100%
    const cappedProgress = progress > 100 ? 100 : progress;
    return (
      <div className="stat-card">
        <div className="stat-header">
          <span className="stat-label">{label}</span>
          <span className="stat-value">{value}</span>
        </div>
        <div className="progress-bar">
          <div
            className={`progress-fill ${colorClass}`}
            style={{ width: `${cappedProgress}%` }}
          ></div>
        </div>
      </div>
    );
  };


  return (
    <div className="card statistics">
      <h2 className="card-title">Statistics</h2>
      <div className="statistics-grid">
        <StatCard
          label="Today"
          value={`${formatHours(statistics.today)} / ${formatHours(statistics.expectedToday)}`}
          colorClass="purple"
          progress={
            statistics.expectedToday > 0
              ? (statistics.today / statistics.expectedToday) * 100
              : 0
          }
        />
        <StatCard
          label="This Week"
          value={`${formatHours(statistics.week)} / ${formatHours(statistics.expectedWeek)}`}
          colorClass="blue"
          progress={
            statistics.expectedWeek > 0
              ? (statistics.week / statistics.expectedWeek) * 100
              : 0
          }
        />
        <StatCard
          label="This Month"
          value={`${formatHours(statistics.month)} / ${formatHours(statistics.expectedMonth)}`}
          colorClass="green"
          progress={
            statistics.expectedMonth > 0
              ? (statistics.month / statistics.expectedMonth) * 100
              : 0
          }
        />
        <StatCard
          label="Overtime"
          value={`${formatHours(statistics.overtime)}`}
          colorClass="orange"
          progress={Math.min((statistics.overtime / 40) * 100, 100)}
        />
      </div>
    </div>
  );
};

export default StatisticsPage;
